// Best-effort client telemetry for the chat UI (#3161).
//
// `track` queues a small event; the queue is posted to /telemetry/events in
// batches (every FLUSH_INTERVAL_MS, at MAX_BATCH events, and on pagehide with
// `keepalive` so the last batch survives navigation). Failures are dropped —
// never retried, never surfaced. Events carry ids and small scalars only: the
// server enforces an allowlist, and nothing here ever receives message text.
// Stored rows join against ChatTurnMetric by message_id so a server-side
// "completed" can be told apart from a client that never rendered it.

type Scalar = string | number | boolean;

export interface TelemetryFields {
  thread_id?: string;
  message_id?: string;
  [key: string]: Scalar | undefined;
}

interface QueuedEvent {
  event: string;
  thread_id?: string;
  message_id?: string;
  payload: Record<string, Scalar>;
  occurred_at: string;
}

export const FLUSH_INTERVAL_MS = 5_000;
export const MAX_BATCH = 20;
const ENDPOINT = "/telemetry/events";

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;

function csrfToken(): string {
  if (typeof document === "undefined") return "";
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

export function track(event: string, fields: TelemetryFields = {}): void {
  const { thread_id, message_id, ...rest } = fields;
  const payload: Record<string, Scalar> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined) payload[key] = value;
  }
  queue.push({ event, thread_id, message_id, payload, occurred_at: new Date().toISOString() });
  if (queue.length >= MAX_BATCH) {
    flush();
  } else if (timer === undefined) {
    timer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

export function flush(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (queue.length === 0 || typeof fetch === "undefined") return;
  const events = queue;
  queue = [];
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ events }),
    }).catch(() => undefined);
  } catch {
    // A synchronous fetch failure (e.g. keepalive body too large) is dropped
    // like any other: telemetry never becomes the user's problem.
  }
}

/** Number of events waiting to be sent. Exposed for tests. */
export function pendingCount(): number {
  return queue.length;
}

export function __resetTelemetryForTests(): void {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  queue = [];
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
}
